<template>
  <div v-if="modelValue && fellow" class="fellow-preview-overlay" @click.self="close">
    <div class="fellow-preview-dialog" role="dialog" aria-modal="true" aria-label="Fellow profile preview">
      <div class="dialog-header">
        <div class="header-profile">
          <div class="avatar-wrap">
            <img
              class="avatar"
              :src="resolvedAvatar"
              :alt="displayName"
              @error="handleAvatarError"
            >
            <div class="avatar-status"></div>
          </div>
          <div class="header-copy">
            <h4 class="title">{{ displayName }}</h4>
            <p class="subtitle">{{ currentJobTitle }}</p>
          </div>
        </div>
        <button type="button" class="close-btn" @click="close" aria-label="Close preview">
          &times;
        </button>
      </div>

      <div class="dialog-body">
        <div class="tab-nav">
          <button
            type="button"
            class="tab-btn"
            :class="{ active: activeTab === 'personal' }"
            @click="activeTab = 'personal'"
          >
            Personal Information
          </button>
          <button
            type="button"
            class="tab-btn"
            :class="{ active: activeTab === 'fellowship' }"
            @click="activeTab = 'fellowship'"
          >
            Fellowship Details
          </button>
        </div>

        <div v-if="activeTab === 'personal'" class="content-pane">
          <div class="grid">
            <div class="info-box">
              <p class="label">Full Name</p>
              <p class="value">{{ displayName }}</p>
            </div>

            <div class="info-box" v-if="fellow.country">
              <p class="label">Country</p>
              <p class="value">{{ countryName }}</p>
            </div>

            <div class="info-box" v-if="fellowDetails.current_organisation">
              <p class="label">Current Organisation</p>
              <p class="value">{{ fellowDetails.current_organisation }}</p>
            </div>

            <div class="info-box" v-if="fellowDetails.current_job_title">
              <p class="label">Current Job Title</p>
              <p class="value">{{ fellowDetails.current_job_title }}</p>
            </div>

            <div class="info-box" v-if="fellow.fellowship_year">
              <p class="label">Fellowship Year</p>
              <p class="value">{{ fellow.fellowship_year }}</p>
            </div>
          </div>

          <div v-if="publications.length" class="section-block">
            <h5 class="section-title">Publications ({{ publications.length }})</h5>
            <div class="publication-list">
              <div
                v-for="(publication, index) in publications"
                :key="`publication-${index}`"
                class="publication-card"
              >
                <button
                  type="button"
                  class="publication-header"
                  @click="togglePublication(index)"
                >
                  <span>{{ publication.title || `Publication ${index + 1}` }}</span>
                  <span>{{ expandedPublicationIndexes.includes(index) ? '-' : '+' }}</span>
                </button>

                <div v-if="expandedPublicationIndexes.includes(index)" class="publication-body">
                  <p v-if="publication.journal"><strong>Journal:</strong> {{ publication.journal }}</p>
                  <p v-if="publication.date"><strong>Date:</strong> {{ formatDate(publication.date) }}</p>
                  <p v-if="publication.keywords"><strong>Keywords:</strong> {{ publication.keywords }}</p>

                  <div v-if="validAuthors(publication).length" class="authors-section">
                    <button
                      type="button"
                      class="authors-toggle"
                      @click="toggleAuthors(index)"
                    >
                      <strong>Authors ({{ validAuthors(publication).length }})</strong>
                      <span>{{ expandedAuthorsIndexes.includes(index) ? '\u2212' : '+' }}</span>
                    </button>
                    <ul v-if="expandedAuthorsIndexes.includes(index)" class="inline-list authors-list">
                      <li v-for="(author, authorIndex) in validAuthors(publication)" :key="`author-${authorIndex}`">
                        {{ authorDisplayName(author) }}
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'fellowship'" class="content-pane">
          <div v-if="fellowshipEntries.length" class="section-block">
            <div v-if="fellowshipEntries.length > 1" class="fellowship-tabs">
              <button
                v-for="(entry, index) in fellowshipEntries"
                :key="`fellowship-tab-${index}`"
                type="button"
                class="tab-btn compact"
                :class="{ active: activeFellowshipIndex === index }"
                @click="activeFellowshipIndex = index"
              >
                Fellowship {{ index + 1 }}
              </button>
            </div>

            <div class="grid" v-if="activeFellowshipEntry">
              <div class="info-box" v-if="activeFellowshipEntry.edctp_project_title">
                <p class="label">Project Title</p>
                <p class="value">{{ activeFellowshipEntry.edctp_project_title }}</p>
              </div>

              <div class="info-box" v-if="activeFellowshipEntry.edctp_project_call">
                <p class="label">Fellowship Type</p>
                <p class="value">{{ activeFellowshipEntry.edctp_project_call }}</p>
              </div>

              <div class="info-box" v-if="activeFellowshipEntry.edctp_hosting_institution_name">
                <p class="label">Hosting Institution</p>
                <p class="value">{{ activeFellowshipEntry.edctp_hosting_institution_name }}</p>
              </div>

              <div class="info-box" v-if="activeFellowshipEntry.edctp_project_start_date || activeFellowshipEntry.edctp_project_end_date">
                <p class="label">Project Duration</p>
                <p class="value">{{ formatDuration(activeFellowshipEntry) }}</p>
              </div>

              <div class="info-box" v-if="activeFellowshipEntry.research_theme">
                <p class="label">Research Theme</p>
                <p class="value">{{ activeFellowshipEntry.research_theme }}</p>
              </div>

              <div class="info-box" v-if="activeFellowshipEntry.project_status">
                <p class="label">Status</p>
                <p class="value">{{ activeFellowshipEntry.project_status }}</p>
              </div>
            </div>
          </div>

          <p v-else class="empty-copy">No fellowship details provided.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'FellowPreviewDialog',
  props: {
    modelValue: {
      type: Boolean,
      default: false
    },
    fellow: {
      type: Object,
      default: null
    },
    avatarResolver: {
      type: Function,
      default: null
    },
    avatarErrorHandler: {
      type: Function,
      default: null
    }
  },
  emits: ['update:modelValue'],
  data() {
    return {
      activeTab: 'personal',
      activeFellowshipIndex: 0,
      expandedPublicationIndexes: [],
      expandedAuthorsIndexes: []
    }
  },
  computed: {
    displayName() {
      if (!this.fellow) return 'Unknown Fellow'
      return this.fellow.full_name || this.fellow.name || 'Unknown Fellow'
    },
    fellowDetails() {
      return this.fellow?.fellow_details || {}
    },
    currentJobTitle() {
      return this.fellowDetails.current_job_title || 'None Provided'
    },
    countryName() {
      return this.resolveCountryName(this.fellow?.country) || 'Not provided'
    },
    resolvedAvatar() {
      if (!this.fellow) return ''
      if (this.avatarResolver) return this.avatarResolver(this.fellow)
      return this.fellow.avatar_url || this.fellow.avatar || ''
    },
    publications() {
      const rawPublications = this.fellowDetails.publications
      if (!rawPublications) return []

      if (Array.isArray(rawPublications)) return rawPublications

      if (typeof rawPublications === 'string') {
        try {
          const parsed = JSON.parse(rawPublications)
          return Array.isArray(parsed) ? parsed : []
        } catch (error) {
          return []
        }
      }

      return []
    },
    fellowshipEntries() {
      const details = this.fellow?.fellowship_details

      if (Array.isArray(details)) return details
      if (details && typeof details === 'object') return [details]

      return []
    },
    activeFellowshipEntry() {
      return this.fellowshipEntries[this.activeFellowshipIndex] || null
    }
  },
  watch: {
    modelValue(isOpen) {
      if (!isOpen) return
      this.activeTab = 'personal'
      this.activeFellowshipIndex = 0
      this.expandedPublicationIndexes = []
      this.expandedAuthorsIndexes = []
    },
    fellow() {
      this.activeTab = 'personal'
      this.activeFellowshipIndex = 0
      this.expandedPublicationIndexes = []
      this.expandedAuthorsIndexes = []
    }
  },
  methods: {
    handleAvatarError(event) {
      if (!event?.target) return
      if (this.avatarErrorHandler) {
        this.avatarErrorHandler(event, this.fellow)
      }
    },
    close() {
      this.$emit('update:modelValue', false)
    },
    togglePublication(index) {
      const openIndex = this.expandedPublicationIndexes.indexOf(index)
      if (openIndex > -1) {
        this.expandedPublicationIndexes.splice(openIndex, 1)
        return
      }
      this.expandedPublicationIndexes.push(index)
    },
    toggleAuthors(index) {
      const openIndex = this.expandedAuthorsIndexes.indexOf(index)
      if (openIndex > -1) {
        this.expandedAuthorsIndexes.splice(openIndex, 1)
        return
      }
      this.expandedAuthorsIndexes.push(index)
    },
    isValidAuthor(author) {
      if (!author) return false
      const firstName = (author.first_name || '').trim().toLowerCase()
      const lastName = (author.last_name || '').trim().toLowerCase()
      const name = (author.name || '').trim().toLowerCase()
      const citation = (author.citation || '').trim().toLowerCase()
      const unknown = ['unknown', 'author unknown', 'unknown author', 'n/a', '']
      if (firstName && !unknown.includes(firstName)) return true
      if (lastName && !unknown.includes(lastName)) return true
      if (name && !unknown.includes(name)) return true
      if (citation && !unknown.includes(citation)) return true
      return false
    },
    validAuthors(publication) {
      if (!publication.authors || !Array.isArray(publication.authors)) return []
      return publication.authors.filter(a => this.isValidAuthor(a))
    },
    authorDisplayName(author) {
      if (author.first_name || author.last_name) {
        return [author.first_name, author.last_name].filter(Boolean).join(' ')
      }
      return author.name || author.citation || ''
    },
    formatDate(dateString) {
      if (!dateString) return 'Not provided'

      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      } catch (error) {
        return dateString
      }
    },
    formatDuration(entry) {
      const start = entry.edctp_project_start_date ? this.formatDate(entry.edctp_project_start_date) : null
      const end = entry.edctp_project_end_date ? this.formatDate(entry.edctp_project_end_date) : null

      if (start && end) return `${start} - ${end}`
      if (start) return `From ${start}`
      if (end) return `Until ${end}`

      return 'Not provided'
    },
    resolveCountryName(country) {
      if (!country) return null

      const countryValue = String(country).trim()
      const countryMap = {
        ZA: 'South Africa',
        TZ: 'Tanzania',
        UG: 'Uganda',
        KE: 'Kenya',
        NG: 'Nigeria',
        GH: 'Ghana',
        ZM: 'Zambia',
        MZ: 'Mozambique',
        ET: 'Ethiopia',
        SN: 'Senegal'
      }

      return countryMap[countryValue.toUpperCase()] || countryValue
    }
  }
}
</script>

<style scoped>
.fellow-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.fellow-preview-dialog {
  width: min(1080px, 100%);
  max-height: calc(100vh - 48px);
  overflow: auto;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 16px 60px rgba(0, 0, 0, 0.25);
}

.dialog-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border: none;
  background: linear-gradient(135deg, #1a8090 0%, #26d0ce 100%);
  position: relative;
}

.header-profile {
  display: flex;
  align-items: center;
  gap: 14px;
}

.avatar-wrap {
  position: relative;
}

.avatar {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  border: 3px solid rgba(255, 255, 255, 0.3);
}

.avatar-status {
  position: absolute;
  top: -2px;
  right: -2px;
  width: 16px;
  height: 16px;
  background: #28a745;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
}

.title {
  margin: 0;
  font-size: 1.4rem;
  color: #ffffff;
  font-weight: 700;
}

.subtitle {
  margin: 4px 0 0;
  color: rgba(255, 255, 255, 0.9);
}

.close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  border: none;
  background: transparent;
  font-size: 1.75rem;
  line-height: 1;
  color: #ffffff;
  cursor: pointer;
  opacity: 0.8;
}

.close-btn:hover {
  opacity: 1;
}

.dialog-body {
  padding: 24px;
  background: #f8f9fa;
}

.tab-nav {
  display: flex;
  gap: 8px;
  border-bottom: 2px solid #dee2e6;
  padding-bottom: 12px;
  margin-bottom: 16px;
}

.tab-btn {
  border: 1px solid transparent;
  background: transparent;
  color: #6c757d;
  padding: 10px 14px;
  border-radius: 6px 6px 0 0;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}

.tab-btn.active {
  color: #1a8090;
  background: #ffffff;
  border-color: #dee2e6 #dee2e6 #ffffff;
}

.tab-btn.compact {
  padding: 6px 10px;
  font-size: 0.9rem;
}

.grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 12px;
}

.info-box {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 14px;
  background: #ffffff;
}

.label {
  margin: 0 0 6px;
  font-size: 0.75rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #7a8f90;
  font-weight: 700;
}

.value {
  margin: 0;
  color: #2f3f40;
}

.section-block {
  margin-top: 18px;
}

.section-title {
  margin: 0 0 10px;
  font-size: 1rem;
  color: #495057;
}

.publication-list {
  display: grid;
  gap: 8px;
}

.publication-card {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.publication-header {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: none;
  background: #f8f9fa;
  padding: 10px 12px;
  text-align: left;
  cursor: pointer;
}

.publication-body {
  padding: 10px 12px;
}

.publication-body p {
  margin: 0 0 8px;
}

.inline-list {
  margin: 6px 0 0;
  padding-left: 18px;
}

.authors-section {
  margin-top: 10px;
}

.authors-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: #f0f8f9;
  border: 1px solid #d0e8ea;
  border-radius: 6px;
  padding: 6px 10px;
  cursor: pointer;
  text-align: left;
  font-size: 0.875rem;
}

.authors-toggle:hover {
  background: #e3f4f5;
}

.authors-list {
  margin: 4px 0 0;
}

.fellowship-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
  flex-wrap: wrap;
}

.empty-copy {
  margin: 0;
  color: #7a8f90;
}

@media (max-width: 768px) {
  .fellow-preview-overlay {
    padding: 10px;
  }

  .dialog-header,
  .dialog-body {
    padding: 14px;
  }

  .avatar {
    width: 52px;
    height: 52px;
  }

  .title {
    font-size: 1.2rem;
  }
}
</style>
